import React, { useState } from 'react';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import useMerchant from '../../hooks/useMerchant';

import { TrashIcon } from '@heroicons/react/24/outline';
import ReusableButton from '../shared/ReusableButton';
import ConfirmModal from '../shared/ConfirmModal';
import { useTranslation } from 'react-i18next';
import * as merchantService from '../../services/merchantService';

const SingleMerchant: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { merchant, loading, error } = useMerchant(id);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const confirmDelete = (id: number) => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (id: number) => {
    if (id) {
      try {
        await merchantService.deleteMerchant(id);
        navigate('/merchants');
      } catch (error) {
        console.error(t('merchants.errorDeletingMerchant'), error);
      }
    }
    setShowDeleteModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {merchant?.company || t('merchants.title')}
          </h1>
          <p className="text-slate-600 mt-1">
            {merchant?.industry} · {t('merchant.createdAt')}: {merchant && format(new Date(merchant.createdAt), 'dd/MM/yyyy')}
          </p>
        </div>
        {merchant && (
          <ReusableButton
            onClick={() => confirmDelete(merchant.id)}
            icon={<TrashIcon className="size-5" aria-hidden="true" />}
            label={t('common.delete')}
            className="bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all"
            disabled={!merchant}
          />
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-700">{t('common.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Merchant Details */}
      {merchant && (
        <div className="grid gap-6">
          {/* Company Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{t('merchant.company')} Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.company')}</p>
                <p className="text-slate-900">{merchant.company}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.industry')}</p>
                <p className="text-slate-900">{merchant.industry}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.taxId')}</p>
                <p className="text-slate-900">{merchant.taxId}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.locations')}</p>
                <p className="text-slate-900">{merchant.locations}</p>
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-sm font-medium text-slate-500">{t('merchant.address')}</p>
                <p className="text-slate-900">{merchant.address}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.website')}</p>
                <a
                  href={merchant.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {merchant.website}
                </a>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.onlineShop')}</p>
                <p className="text-slate-900">{merchant.onlineShop}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.director')}</p>
                <p className="text-slate-900">{merchant.director}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.contactPerson')}</p>
                <p className="text-slate-900">{merchant.contactPerson}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.email')}</p>
                <a
                  href={`mailto:${merchant.email}`}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {merchant.email}
                </a>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.telephone')}</p>
                <p className="text-slate-900">{merchant.telephone}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.mobile')}</p>
                <p className="text-slate-900">{merchant.mobile}</p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Financial Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.avgTxValue')}</p>
                <p className="text-slate-900">{merchant.avgTxValue}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.currMonthVol')}</p>
                <p className="text-slate-900">{merchant.currMonthVol}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-500">{t('merchant.projMonthVolAcct')}</p>
                <p className="text-slate-900">{merchant.projMonthVolAcct}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleConfirmDelete(Number(id))}
          title={t('common.confirmAction')}
          message={t('common.confirmActionMessage')}
        />
      )}
    </div>
  );
};

export default SingleMerchant;
